import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useApp } from "@/contexts/AppContext";
import api from "@/lib/api";
import { CheckCircle, Circle, ArrowRight, ArrowLeft, Building2, Upload, ClipboardList, Calculator, Users, FileText, Rocket } from "lucide-react";

const STEPS = [
  { step: 1, label: "Group Profile", icon: Building2, desc: "Set your group name, legal details, year-end", path: "/settings" },
  { step: 2, label: "Add Entities", icon: Building2, desc: "Add your subsidiary companies", path: "/settings" },
  { step: 3, label: "Import Assets", icon: Upload, desc: "Import your asset register from CSV", path: "/import" },
  { step: 4, label: "Review Policies", icon: ClipboardList, desc: "Check depreciation policies per category", path: "/policies" },
  { step: 5, label: "Run Depreciation", icon: Calculator, desc: "Process your first monthly depreciation", path: "/depreciation" },
  { step: 6, label: "Invite Team", icon: Users, desc: "Add users with appropriate roles", path: "/users" },
  { step: 7, label: "Review & Go Live", icon: Rocket, desc: "Check AFS Note, lock period, go live", path: "/far" },
];

export default function OnboardingPage() {
  const { loadEntities, loadCategories } = useApp();
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeStep, setActiveStep] = useState(1);
  const navigate = useNavigate();

  useEffect(() => {
    const load = async () => {
      try {
        const { data } = await api.get("/onboarding/status");
        setStatus(data);
        // Find first incomplete step
        const first = data.steps.find(s => !s.done);
        if (first) setActiveStep(first.step);
      } catch (e) { console.error(e); }
      finally { setLoading(false); }
    };
    load();
  }, []);

  const goToStep = (step) => {
    const s = STEPS.find(x => x.step === step);
    if (s) navigate(s.path);
  };

  if (loading) return <div className="stratum-card p-12 text-center text-[#6B7280]">Loading setup wizard...</div>;

  const pct = status?.progress_pct || 0;

  return (
    <div className="space-y-6" data-testid="onboarding-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-display text-2xl font-semibold text-[#0B1020]">
            <Rocket size={24} className="inline mr-2 text-[#C8A961]" />
            Setup Wizard
          </h1>
          <p className="text-sm text-[#6B7280] mt-1">7 steps to get your asset register up and running</p>
        </div>
        <div className="text-right">
          <div className="font-mono text-2xl font-semibold text-[#C8A961] tabular-nums">{pct}%</div>
          <div className="text-xs text-[#6B7280]">{status?.completed || 0} of 7 complete</div>
        </div>
      </div>

      {/* Progress Bar */}
      <div className="stratum-card p-4">
        <div className="h-3 bg-[#E8E2D5] rounded-full overflow-hidden">
          <div className="h-full bg-[#C8A961] rounded-full transition-all duration-1000" style={{ width: `${pct}%` }} />
        </div>
      </div>

      {/* Steps */}
      <div className="grid grid-cols-1 lg:grid-cols-7 gap-2">
        {STEPS.map((s) => {
          const stepStatus = status?.steps?.find(x => x.step === s.step);
          const done = stepStatus?.done;
          const Icon = s.icon;
          const isActive = activeStep === s.step;
          return (
            <button key={s.step} onClick={() => setActiveStep(s.step)}
              className={`stratum-card p-3 text-center transition-all ${isActive ? "ring-2 ring-[#C8A961] shadow-md" : ""} ${done ? "bg-[#065F46]/5" : ""}`}
              data-testid={`wizard-step-${s.step}`}>
              <div className={`w-8 h-8 rounded-full mx-auto mb-2 flex items-center justify-center ${done ? "bg-[#065F46] text-white" : isActive ? "bg-[#C8A961] text-white" : "bg-[#E8E2D5] text-[#6B7280]"}`}>
                {done ? <CheckCircle size={16} /> : <span className="font-mono text-xs font-bold">{s.step}</span>}
              </div>
              <p className="text-[10px] font-medium text-[#0B1020] leading-tight">{s.label}</p>
            </button>
          );
        })}
      </div>

      {/* Active Step Detail */}
      {STEPS.map((s) => {
        if (s.step !== activeStep) return null;
        const stepStatus = status?.steps?.find(x => x.step === s.step);
        const done = stepStatus?.done;
        const Icon = s.icon;
        return (
          <div key={s.step} className="stratum-card p-8" data-testid={`wizard-detail-${s.step}`}>
            <div className="flex items-start gap-6">
              <div className={`w-16 h-16 rounded-2xl flex items-center justify-center flex-shrink-0 ${done ? "bg-[#065F46]/10" : "bg-[#C8A961]/10"}`}>
                <Icon size={28} className={done ? "text-[#065F46]" : "text-[#C8A961]"} />
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-3 mb-2">
                  <h2 className="font-display text-xl font-semibold text-[#0B1020]">Step {s.step}: {s.label}</h2>
                  {done && <span className="px-2 py-0.5 rounded-full bg-[#065F46]/10 text-[#065F46] text-xs font-medium">Complete</span>}
                </div>
                <p className="text-sm text-[#6B7280] mb-4">{stepStatus?.desc || s.desc}</p>

                {/* Step-specific content */}
                {s.step === 1 && <p className="text-sm text-[#1A1A1A]">Go to Settings to set your group's legal name, registration number, VAT number, financial year-end date, functional currency, and auditor name. This information appears on all generated reports and policy documents.</p>}
                {s.step === 2 && <p className="text-sm text-[#1A1A1A]">Add each subsidiary company as an entity. You can do this manually in Settings &gt; Entities, or entities will be auto-created when you import assets from CSV with an Entity/Division column.</p>}
                {s.step === 3 && <p className="text-sm text-[#1A1A1A]">Import your existing Fixed Asset Register. Prepare a CSV file with columns for Entity/Division, Asset Tag, Description, Category, Acquired Date, Original Cost, Accumulated Depreciation, Useful Life, and Residual Value. Stratum auto-detects column names and maps categories to IFRS standards.</p>}
                {s.step === 4 && <p className="text-sm text-[#1A1A1A]">Review the depreciation policy for each asset category. Set the depreciation method (straight-line, diminishing balance, etc.), useful life in months, and residual value percentage. These settings drive the depreciation engine and the auto-generated AFS policy document. Use the AI "Stratum Recommends" feature for suggestions.</p>}
                {s.step === 5 && <p className="text-sm text-[#1A1A1A]">Select an entity from the sidebar, navigate to Depreciation, review the schedule, and click "Run Monthly Depreciation". This posts balanced journal entries (DR Depreciation Expense / CR Accumulated Depreciation) and updates every asset's carrying amount. Download the journal as CSV for your accounting system.</p>}
                {s.step === 6 && <p className="text-sm text-[#1A1A1A]">Add your team members. Assign roles: CFO (full access per entity), Asset Manager (assets and utilisation), Accountant (read-only, journals), Auditor (read-only across all entities), or other roles. Each user gets their own login credentials.</p>}
                {s.step === 7 && <p className="text-sm text-[#1A1A1A]">Review the Full FAR (AFS Note) to verify the IAS 16.73 reconciliation. Check the Health Score to ensure compliance. Lock the period to prevent backdated transactions. Generate the PPE Accounting Policy document. Your asset register is now live and audit-ready.</p>}

                <div className="flex gap-3 mt-6">
                  {s.step > 1 && (
                    <button onClick={() => setActiveStep(s.step - 1)} className="px-4 py-2 rounded-lg border border-[#E8E2D5] text-sm hover:bg-[#FAF8F4] flex items-center gap-2">
                      <ArrowLeft size={14} /> Previous
                    </button>
                  )}
                  <button onClick={() => goToStep(s.step)} className="stratum-btn-primary flex items-center gap-2" data-testid={`go-to-step-${s.step}`}>
                    {done ? "Review" : "Go to " + s.label} <ArrowRight size={14} />
                  </button>
                  {s.step < 7 && (
                    <button onClick={() => setActiveStep(s.step + 1)} className="px-4 py-2 rounded-lg border border-[#E8E2D5] text-sm hover:bg-[#FAF8F4] flex items-center gap-2">
                      Skip <ArrowRight size={14} />
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}
